import React, { useRef } from 'react';
import {
  BlueDot,
  EmptyManagerDot,
  HistoricalLane,
  Tooltip,
  ValueBar,
  VariationSign,
  Wrapper,
  YellowDot,
} from './ColorBar.styles';
import {
  getFlagsPercentage,
  getScoreColor,
  getVariationValues,
  getWidthPercentage,
} from '../../../../../../../helpers/effectReports/chartCounters';
import { IToggleState, Question, Topic } from '../../../../../../../services/reports';
import { MiddleLine } from '../../../ReportTimestemps/TimestampSigns/TimestampSigns.styles';

interface ColorBarProps {
  data: Topic | Question;
  togglersState: IToggleState;
  slim?: boolean;
}

const isTopic = (data: Topic | Question): data is Topic => {
  return 'organisation_value' in data;
};

const ColorBar: React.FC<ColorBarProps> = ({ data, togglersState, slim = false }) => {
  const {
    historical_result,
    organization_mean,
    external_benchmark,
    variation_between_respondents,
    team_manager,
  } = togglersState;

  const { average_value, historical_value, single_respondent_max, single_respondent_min } = data;

  const historicalLaneRef = useRef<HTMLDivElement | null>(null);

  const organisation_value = isTopic(data) ? data.organisation_value : undefined;
  const externalBenchmark = isTopic(data) ? data.external_benchmark : undefined;
  const superManagerBenchmark = isTopic(data) ? data.super_manager_value : undefined;
  const thresholdValues = isTopic(data) ? data.threshold_values : undefined;

  const { variationPosition, variationWidth } = getVariationValues(
    single_respondent_min ?? 0,
    single_respondent_max ?? 0
  );

  return (
    <Wrapper isTopic={isTopic(data) || slim}>
      <ValueBar
        data-testid="value-bar"
        color={getScoreColor(
          average_value,
          thresholdValues?.low,
          thresholdValues?.high,
          thresholdValues?.medium
        )}
        width={getWidthPercentage(average_value ?? 0)}
      />
      {historical_result && (
        <HistoricalLane
          data-testid="historical-stamp"
          value={getFlagsPercentage(historical_value)}
          ref={historicalLaneRef}
        >
          <Tooltip>{historical_value || '0'}</Tooltip>
        </HistoricalLane>
      )}
      {variation_between_respondents &&
        single_respondent_min != null &&
        single_respondent_max != null && (
        <VariationSign
          data-testid="variation-stamp"
          left={variationPosition}
          width={variationWidth}
        >
          <Tooltip>
            {`${(single_respondent_min ?? 0).toFixed(1)} - ${(single_respondent_max ?? 0).toFixed(
              1
            )}`}
          </Tooltip>
          <MiddleLine />
        </VariationSign>
      )}
      {isTopic(data) && (
        <>
          {organization_mean && (
            <BlueDot data-testid="org-mean-stamp" value={getFlagsPercentage(organisation_value)}>
              <Tooltip>{organisation_value || '0'}</Tooltip>
            </BlueDot>
          )}
          {external_benchmark && (
            <YellowDot
              data-testid="external-bench-stamp"
              value={getFlagsPercentage(externalBenchmark)}
            >
              <Tooltip>{externalBenchmark || '0'}</Tooltip>
            </YellowDot>
          )}
          {team_manager && (
            <EmptyManagerDot
              data-testid="team-manager-stamp"
              value={getFlagsPercentage(superManagerBenchmark)}
            >
              <Tooltip>{superManagerBenchmark || '0'}</Tooltip>
            </EmptyManagerDot>
          )}
        </>
      )}
    </Wrapper>
  );
};

export default ColorBar;
